/**
 * Tenant utilities for subdomain-based multi-tenancy
 */

const PROD_DOMAIN = 'radiancebook.com';
const LOCAL_DOMAIN = 'radiancebook.local';
const VERCEL_DOMAIN = 'radiancebook-emr.vercel.app';

/**
 * Extract subdomain from current hostname
 * @returns {string|null} Subdomain or null if not found
 */
export function getSubdomain() {
  const hostname = window.location.hostname;

  // Remove port if present
  const host = hostname.split(':')[0];

  // Check if this is localhost or IP address
  if (host === 'localhost' || /^\d+\.\d+\.\d+\.\d+$/.test(host)) {
    return null;
  }

  // Define root domains that should NOT be treated as having subdomains
  const rootDomains = [
    VERCEL_DOMAIN,
    PROD_DOMAIN,
    LOCAL_DOMAIN,
  ];

  // Check if this is a root domain
  if (rootDomains.includes(host)) {
    return null;
  }

  // Split host into parts
  const parts = host.split('.');

  // Need at least 3 parts for a subdomain (subdomain.domain.tld)
  if (parts.length < 3) {
    return null;
  }

  // Check if the base domain (without the first part) is a known root domain
  const baseDomain = parts.slice(1).join('.');
  if (rootDomains.includes(baseDomain)) {
    // This is a subdomain of a known root domain (e.g., clinic1.radiancebook.com)
    return parts[0];
  }

  // For Vercel preview deployments and other cases
  // If it ends with .vercel.app and doesn't have more than 3 parts, it's a root domain
  if (host.endsWith('.vercel.app') && parts.length === 3) {
    return null;
  }

  // First part is the subdomain
  return parts[0];
}

/**
 * Check if running in multi-tenant mode (has subdomain)
 * @returns {boolean}
 */
export function isMultiTenantMode() {
  return getSubdomain() !== null;
}

/**
 * Get the full tenant URL for a given subdomain
 * @param {string} subdomain
 * @returns {string}
 */
export function getTenantUrl(subdomain) {
  const protocol = window.location.protocol;
  const port = window.location.port ? `:${window.location.port}` : '';

  if (window.location.hostname.includes('.local')) {
    return `${protocol}//${subdomain}.${LOCAL_DOMAIN}${port}`;
  }

  if (window.location.hostname.includes('.vercel.app')) {
    return `${protocol}//${subdomain}.${VERCEL_DOMAIN}${port}`;
  }

  return `${protocol}//${subdomain}.${PROD_DOMAIN}${port}`;
}

/**
 * Validate that user is accessing via proper tenant subdomain
 * Redirects to error page if accessing without subdomain in production
 */
export function validateTenantAccess() {
  const subdomain = getSubdomain();

  // In development on localhost, allow access without subdomain
  if (window.location.hostname === 'localhost') {
    console.warn('⚠️ Running without tenant subdomain. Use clinic1.radiancebook.local for testing multitenancy.');
    return true;
  }

  // In production/staging, require subdomain
  if (!subdomain) {
    console.error('❌ No tenant subdomain detected');
    return false;
  }

  return true;
}
